// How a Flow (BPMN) is dressed in the product's own colours.
//
// Everything here is a pure decision - which family a BPMN element belongs to,
// which object it stands for, what colour that object's type is, and where a
// marker goes on the shape. BpmnCanvas does the drawing; keeping the decisions
// out of it means they can be read, and tested, without a canvas.
//
// No type, colour or customer convention is written down here: a colour only
// ever comes from the view configuration (`typeInfo`), and an element we can't
// tie to an object keeps the neutral brand ink.

import { brand } from "../../theme/businessFlowsTheme";
import { tint, typeInfo } from "../../utils/viewConfig";

// The BPMN element types that can carry a diagram of their own, and so can be
// opened one level deeper. Kept for anything that still reads a list.
export const DRILLABLE = [
  "bpmn:Task",
  "bpmn:SubProcess",
  "bpmn:CallActivity",
  "bpmn:UserTask",
  "bpmn:ServiceTask",
  "bpmn:ManualTask",
];

// One family per group of shapes that should look alike. The families are the
// only thing the stylesheet knows about, so a BPMN type the library adds later
// still lands somewhere sensible (or, unknown, keeps the library's own look).
export function shapeFamily(type = "") {
  const name = String(type || "");
  if (!name.startsWith("bpmn:")) return "";
  const kind = name.slice(5);
  if (kind === "Participant") return "participant";
  if (kind === "Lane") return "lane";
  if (kind === "CallActivity") return "call";
  if (kind === "SubProcess" || kind === "Transaction" || kind === "AdHocSubProcess") return "subprocess";
  if (kind.endsWith("Task")) return "task";
  if (kind === "StartEvent") return "start";
  if (kind === "EndEvent") return "end";
  if (kind.endsWith("Event")) return "event";
  if (kind.endsWith("Gateway")) return "gateway";
  if (kind === "SequenceFlow") return "sequence";
  if (kind === "MessageFlow") return "message";
  if (kind.endsWith("Association")) return "association";
  if (kind.startsWith("Data")) return "data";
  if (kind === "Group") return "group";
  if (kind === "TextAnnotation") return "note";
  return "";
}

// The families drawn as a box, which is where an accent bar belongs.
const BOXES = new Set(["task", "subprocess", "call"]);
export const isBox = (family) => BOXES.has(family);

// Whether an element could stand for an object with a diagram of its own -
// every activity, whatever the exporter called it. Derived from the family
// rather than a list of names, so a `bpmn:ScriptTask`, a `bpmn:SendTask` or a
// `bpmn:Transaction` in somebody else's library is offered the same way in,
// without a name of theirs being written down here.
export const isDrillableType = (type) => isBox(shapeFamily(type));

const key = (value) => String(value === undefined || value === null ? "" : value).trim().toLowerCase();

// The objects this diagram's reader already knows about - the object the
// diagram belongs to and the parts fetched beside it - indexed the two ways a
// BPMN element can name one: the GUID its id carries, and its name.
export function indexObjects(objects = []) {
  const byGuid = new Map();
  const byName = new Map();
  (objects || []).forEach((object) => {
    if (!object) return;
    const entry = {
      guid: object.GUID || object.guid || "",
      name: object.name || "",
      objectType: object.Type || object.type || "",
    };
    if (!entry.objectType) return;
    const guid = key(entry.guid);
    const name = key(entry.name);
    if (guid && !byGuid.has(guid)) byGuid.set(guid, entry);
    // First one wins: two objects of the same name are a tie we don't try to
    // break, and the GUID above is the answer wherever the id carries one.
    if (name && !byName.has(name)) byName.set(name, entry);
  });
  return { byGuid, byName };
}

export const EMPTY_INDEX = indexObjects([]);

// The object an element stands for, where we know one.
export function objectFor(index, element = {}) {
  const byGuid = index?.byGuid || EMPTY_INDEX.byGuid;
  const byName = index?.byName || EMPTY_INDEX.byName;
  return byGuid.get(key(element.guid)) || byName.get(key(element.name)) || null;
}

// The colours one element is drawn in.
// - an element that names an object we know: that object's type colour, as the
//   view configuration gives it (the same colour the map's cards wear);
// - an element that only carries a diagram of its own: the neutral brand ink,
//   so the drill-down affordance still reads as deliberate;
// - anything else: nothing, and the shape keeps the neutral sheet look.
export function accentFor(config, index, element = {}) {
  const found = objectFor(index, element);
  if (!found && !element.hasDiagram) return null;
  const info = found ? typeInfo(config, found.objectType) : null;
  const colour = info ? info.colour : brand.ink;
  return {
    objectType: found ? found.objectType : "",
    label: info ? info.label : "",
    colour,
    wash: tint(colour, 0.1),
    line: tint(colour, 0.34),
  };
}

// What the browser's own tooltip says about an element: its name, the type of
// the object it stands for where we know it, and the way in where it has a
// diagram of its own.
export function describeElement(element = {}, accent) {
  const lines = [];
  const name = String(element.name || "").trim();
  if (name) lines.push(accent && accent.label ? `${name} · ${accent.label}` : name);
  else if (accent && accent.label) lines.push(accent.label);
  if (element.hasDiagram) lines.push("Double-click, or press Enter, to open this step's diagram");
  return lines.join("\n");
}

// A slim accent pill inside a box's left edge - the marker the map's cards
// wear, so the same object reads the same way in both. Null where the shape is
// too small to carry one without crowding its own label.
export function accentBar(element = {}) {
  const width = Number(element.width) || 0;
  const height = Number(element.height) || 0;
  if (width < 60 || height < 30) return null;
  const inset = Math.min(10, Math.max(5, Math.round(height * 0.2)));
  return { x: 4, y: inset, width: 3.5, height: Math.max(8, height - inset * 2), rx: 1.75 };
}

// The strip bpmn-js leaves for a pool's or lane's title, so a header can be
// painted as a header rather than guessed from a vertical line.
export const HEADER_BAND = 30;

export function headerBand(element = {}) {
  const width = Number(element.width) || 0;
  const height = Number(element.height) || 0;
  if (width < HEADER_BAND * 2 || height < HEADER_BAND) return null;
  // bpmn-js puts the title on the left of a wide pool and along the top of a
  // tall one; the band follows it.
  return width >= height
    ? { x: 0, y: 0, width: HEADER_BAND, height }
    : { x: 0, y: 0, width, height: HEADER_BAND };
}

// BPMN element ids carry the object GUID as "ID-<guid>".
export const guidFromElementId = (id = "") => String(id || "").replace(/^ID-/, "");

// Whether an element's id is an id the library could answer for: a GUID an
// exporter wrote into it, or the stand-in the product gives an object that was
// imported without one. A step called "t1" or "Task_04ka" is a step in
// somebody's file and nothing else, and asking the database about it is a
// round trip that can only come back empty.
const OBJECT_ID = /^(\{)?[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}(\})?$/i;
export const looksLikeObjectId = (id = "") => {
  const value = String(id || "").trim();
  return OBJECT_ID.test(value) || /^nid-\d+$/.test(value);
};
